import {
  defaultShouldDehydrateQuery,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import SuperJSON from "superjson";

import { PROTECTED_ROUTE_PREFIXES, matchesRoutePrefix } from "@/lib/constants";
import {
  getSessionBackendAccessToken,
  isBackendUnauthorizedError,
  isUnauthorizedTRPCError,
} from "@/lib/trpc-errors";

/**
 * Protected tRPC procedures surface backend 401s as UNAUTHORIZED. When the
 * NextAuth session still exists but its embedded backend JWT is rejected,
 * sign the user out so they can log in again with a fresh token.
 */
let sessionCheckScheduled = false;
let lastConfirmedSessionAt = 0;

const SESSION_CONFIRMATION_COOLDOWN_MS = 30 * 1000;
const SESSION_MISSING_RECHECK_MS = 700;

// Survives query-client remounts so a 401 burst can't trigger repeated signouts.
export const SIGNOUT_IN_FLIGHT_KEY = "zom.auth.signoutInFlight";

function signoutInFlight(): boolean {
  try {
    return window.sessionStorage.getItem(SIGNOUT_IN_FLIGHT_KEY) === "1";
  } catch {
    return false;
  }
}

function markSignoutInFlight(): void {
  try {
    window.sessionStorage.setItem(SIGNOUT_IN_FLIGHT_KEY, "1");
  } catch {
    // ignore storage errors (private mode, etc.)
  }
}

function handleUnauthorizedError(error: unknown): void {
  if (typeof window === "undefined") return;
  if (sessionCheckScheduled || signoutInFlight()) return;

  // Only a real backend token rejection forces logout, not a transient session-gate miss.
  if (!isBackendUnauthorizedError(error)) return;

  if (!matchesRoutePrefix(window.location.pathname, PROTECTED_ROUTE_PREFIXES)) {
    return;
  }

  const now = Date.now();
  if (now - lastConfirmedSessionAt < SESSION_CONFIRMATION_COOLDOWN_MS) {
    return;
  }

  sessionCheckScheduled = true;

  setTimeout(() => {
    void (async () => {
      let signoutAttempted = false;
      try {
        const { getSession } = await import("next-auth/react");
        const { signOutFromApp } = await import("@/lib/auth-client");
        const session = await getSession();
        const backendToken = getSessionBackendAccessToken(session);

        if (session && backendToken) {
          signoutAttempted = true;
          markSignoutInFlight();
          await signOutFromApp();
          return;
        }

        if (session) {
          lastConfirmedSessionAt = Date.now();
          return;
        }

        await new Promise((resolve) =>
          window.setTimeout(resolve, SESSION_MISSING_RECHECK_MS),
        );
        const retrySession = await getSession();
        const retryBackendToken = getSessionBackendAccessToken(retrySession);
        if (retrySession && retryBackendToken) {
          signoutAttempted = true;
          markSignoutInFlight();
          await signOutFromApp();
          return;
        }

        if (retrySession) {
          lastConfirmedSessionAt = Date.now();
          return;
        }

        signoutAttempted = true;
        markSignoutInFlight();
        await signOutFromApp();
      } catch {
        // A failed signout must not leave the flag stuck, or future 401s in this
        // tab are silently dropped. Clear it so a later 401 can retry.
        if (signoutAttempted) {
          try {
            window.sessionStorage.removeItem(SIGNOUT_IN_FLIGHT_KEY);
          } catch {
            // ignore storage errors (private mode, etc.)
          }
        }
      } finally {
        sessionCheckScheduled = false;
      }
    })();
  }, 500);
}

export const createQueryClient = () =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: handleUnauthorizedError,
    }),
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        retry: (failureCount, error) => {
          if (isUnauthorizedTRPCError(error)) {
            return false;
          }
          return failureCount < 3;
        },
      },
      mutations: {
        onError: handleUnauthorizedError,
      },
      dehydrate: {
        serializeData: SuperJSON.serialize,
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === "pending",
      },
      hydrate: {
        deserializeData: SuperJSON.deserialize,
      },
    },
  });
